import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { readLocation } from "@/components/LocationPill";
import WeatherPulseNav from "./WeatherPulseNav";
import PageGuide from "@/components/PageGuide";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { MapPin, Plus, Radio } from "@phosphor-icons/react";

const INSTRUMENT_HINTS = ["Thermometer", "Rain gauge", "Barometer", "Hygrometer", "Anemometer", "Wind vane", "UV sensor", "Soil moisture", "Soil temperature"];

/** My Weather Station — register instruments, then the station page charts
 * temperature & rainfall from the station's own reports. */
export default function WeatherStation() {
  const { id } = useParams(); // public view when /weather-pulse/station/:id
  const { user, requireSignIn } = useAuth();
  const [mine, setMine] = useState(null);
  const [station, setStation] = useState(null);
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", instruments: [], sensor_height_m: "", exposure: "", notes: "", station_type: "personal-station" });

  const activeId = id || null;

  useEffect(() => {
    if (activeId) {
      http.get(`/weather-pulse/stations/${activeId}`).then(({ data }) => setStation(data.station)).catch(() => setStation(null));
    } else if (user) {
      http.get("/weather-pulse/stations/mine").then(({ data }) => setMine(data.stations || [])).catch(() => setMine([]));
    }
  }, [activeId, user]);

  useEffect(() => {
    if (!activeId) return;
    http.get(`/weather-pulse/stations/${activeId}/series`, { params: { days } })
      .then(({ data }) => setSeries(data.series || []))
      .catch(() => setSeries([]));
  }, [activeId, days]);

  const createStation = async () => {
    if (!requireSignIn("register a weather station")) return;
    if (f.name.trim().length < 3) return toast.error("Name your station");
    const loc = readLocation();
    try {
      await http.post("/weather-pulse/stations", {
        name: f.name.trim(),
        instruments: f.instruments,
        sensor_height_m: f.sensor_height_m ? parseFloat(f.sensor_height_m) : null,
        exposure: f.exposure || null,
        notes: f.notes || null,
        station_type: f.station_type,
        lat: loc.lat, lon: loc.lon,
        country: loc.country, state: loc.state, city: loc.city,
      });
      toast.success("Station registered");
      setShowForm(false);
      setF({ name: "", instruments: [], sensor_height_m: "", exposure: "", notes: "", station_type: "personal-station" });
      const { data } = await http.get("/weather-pulse/stations/mine");
      setMine(data.stations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't register station");
    }
  };

  // -------- public station page --------
  if (activeId) {
    return (
      <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-station-page">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <WeatherPulseNav />
          {!station ? (
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading station…</div>
          ) : (
            <>
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1 flex items-center gap-1.5">
                <Radio size={13} weight="fill" /> {station.station_type.replace("-", " ")}
              </div>
              <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">{station.name}.</h1>
              <p className="mt-2 text-sm text-[#3A3830]">
                {[station.city, station.state, station.country].filter(Boolean).join(", ")} · run by {station.owner_name} · since {station.installed_at} · {station.observation_count} observations
              </p>
              {station.instruments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {station.instruments.map((i) => (
                    <span key={i} className="rounded-full border border-black/10 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase">{i}</span>
                  ))}
                </div>
              )}

              <div className="mt-6 flex gap-1.5" data-testid="wp-station-range">
                {[7, 30, 90, 365].map((d) => (
                  <button key={d} onClick={() => setDays(d)} data-testid={`wp-station-range-${d}`}
                    className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border ${days === d ? "bg-[#1B4332] text-[#F5EBDC] border-transparent" : "bg-white border-black/15"}`}>
                    {d === 365 ? "1 year" : `${d} days`}
                  </button>
                ))}
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl bg-white border border-black/10 p-4" data-testid="wp-station-temp-chart">
                  <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Temperature °C</div>
                  {series.some((s) => s.temp_avg != null) ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={series}>
                        <XAxis dataKey="date" tick={{ fontSize: 8 }} />
                        <YAxis tick={{ fontSize: 9 }} domain={["auto", "auto"]} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Line type="monotone" dataKey="temp_max" stroke="#dc2626" dot={false} name="max" />
                        <Line type="monotone" dataKey="temp_avg" stroke="#B85F2A" dot={false} name="avg" />
                        <Line type="monotone" dataKey="temp_min" stroke="#0284c7" dot={false} name="min" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#3A3830]/70">No temperature measurements in this window yet.</p>}
                </div>
                <div className="rounded-2xl bg-white border border-black/10 p-4" data-testid="wp-station-rain-chart">
                  <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Rainfall mm</div>
                  {series.some((s) => s.rainfall_mm > 0) ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={series}>
                        <XAxis dataKey="date" tick={{ fontSize: 8 }} />
                        <YAxis tick={{ fontSize: 9 }} />
                        <Tooltip />
                        <Bar dataKey="rainfall_mm" fill="#0284c7" radius={[3, 3, 0, 0]} name="rainfall (mm)" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-[#3A3830]/70">No rainfall measurements in this window yet.</p>}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    );
  }

  // -------- my stations + register --------
  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-my-station">
      <div className="max-w-[900px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ Level 3 observer</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">My weather station.</h1>
        <p className="mt-2 text-sm md:text-base text-[#3A3830] max-w-2xl">
          Have a thermometer, rain gauge or a full automated station? Register it, then tag your reports with it — your station page builds temperature and rainfall charts from your own measurements.
        </p>
        <div className="mt-5"><PageGuide pageKey="weather:station" testidPrefix="wp-station" /></div>

        {!user ? (
          <button onClick={() => requireSignIn("register a weather station")} data-testid="wp-station-signin"
            className="mt-6 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
            Sign in to register
          </button>
        ) : (
          <>
            {mine && mine.length > 0 && (
              <ul className="mt-6 space-y-2" data-testid="wp-station-list">
                {mine.map((s) => (
                  <li key={s.id} className="rounded-xl border border-black/10 bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`wp-station-row-${s.id}`}>
                    <Radio size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{s.name}</div>
                      <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/70">
                        {[s.city, s.country].filter(Boolean).join(", ")} · {(s.instruments || []).length} instruments · since {s.installed_at}
                      </div>
                    </div>
                    <a href={`/weather-pulse/station/${s.id}`} data-testid={`wp-station-open-${s.id}`}
                      className="rounded-full border border-black/15 px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:bg-black/5">Open page</a>
                  </li>
                ))}
              </ul>
            )}

            {!showForm ? (
              <button onClick={() => setShowForm(true)} data-testid="wp-station-new"
                className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                <Plus size={13} weight="bold" /> Register a station
              </button>
            ) : (
              <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5" data-testid="wp-station-form">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                  placeholder="Station name — e.g. Green Valley Farm" data-testid="wp-station-name"
                  className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
                <div className="mt-3 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">Instruments</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {INSTRUMENT_HINTS.map((i) => (
                    <button key={i} type="button"
                      onClick={() => setF({ ...f, instruments: f.instruments.includes(i) ? f.instruments.filter((x) => x !== i) : [...f.instruments, i] })}
                      data-testid={`wp-station-inst-${i.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase border ${f.instruments.includes(i) ? "bg-[#1B4332] text-[#F5EBDC] border-transparent" : "bg-white border-black/15"}`}>
                      {i}
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <input value={f.sensor_height_m} onChange={(e) => setF({ ...f, sensor_height_m: e.target.value })}
                    type="number" step="0.1" placeholder="Sensor height (m, optional)" data-testid="wp-station-height"
                    className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
                  <input value={f.exposure} onChange={(e) => setF({ ...f, exposure: e.target.value })}
                    placeholder="Exposure — e.g. open field, rooftop" data-testid="wp-station-exposure"
                    className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm" />
                </div>
                <p className="mt-2 text-[11px] text-[#3A3830]/70 flex items-center gap-1">
                  <MapPin size={11} weight="fill" /> Location: your current saved location. Public page shows an approximate grid only.
                </p>
                <div className="mt-3 flex gap-2">
                  <button onClick={createStation} data-testid="wp-station-save"
                    className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Register</button>
                  <button onClick={() => setShowForm(false)} className="rounded-full border border-black/15 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
